import { TFunction } from 'i18next'
import { z } from 'zod'

import { ParkingAssignmentTypeEnum } from '@/models'

import { MAX_VEHICLES } from './parking-modals.constants'
import { isEmptyVehicle } from './parking-modals.utils'

export const VehicleFormSchema = z.object({
  color: z.string().optional(),
  // Note: Indicates if the vehicle is persisted in the database or just locally added.
  id: z.number().optional(),
  licensePlate: z.string(),
  make: z.string().optional(),
  model: z.string().optional(),
  permitTag: z.string().optional(),
  // Note: Resident this vehicle belongs to (used in unit assignment mode).
  residentId: z.number().nullable().optional(),
  // Note: Uneditable field. It is added to display source for selected vehicle.
  source: z.string().optional(),
  // Note: Local id added by useForm hook
  vehicleId: z.string().optional(),
  year: z.string().optional(),
})

export type VehicleFormData = z.infer<typeof VehicleFormSchema>

const makeBaseVehiclesArraySchema = (t: TFunction<['parking-management']>) =>
  z
    .array(VehicleFormSchema)
    .min(1)
    .max(
      MAX_VEHICLES,
      t('parking-management:parkingModals.validation.maxVehicles', { max: MAX_VEHICLES }),
    )

const validateVehicles = (
  vehicles: VehicleFormData[],
  ctx: z.RefinementCtx,
  t: TFunction<['parking-management']>,
  pathPrefix: (number | string)[] = [],
) => {
  // Note: A single fully-empty vehicle means that form is empty — skip validations
  if (vehicles.length === 1 && isEmptyVehicle(vehicles[0])) {
    return
  }

  const seenLicensePlates = new Set<string>()

  vehicles.forEach((vehicle, index) => {
    const normalizedLicensePlate = vehicle.licensePlate.trim().toLowerCase()

    if (!normalizedLicensePlate) {
      ctx.addIssue({
        code: 'custom',
        message: t('parking-management:parkingModals.validation.licensePlateRequired'),
        path: [...pathPrefix, index, 'licensePlate'],
      })
      return
    }

    if (normalizedLicensePlate.length < 2 || normalizedLicensePlate.length > 10) {
      ctx.addIssue({
        code: 'custom',
        message: t('parking-management:parkingModals.validation.licensePlateLength'),
        path: [...pathPrefix, index, 'licensePlate'],
      })
      return
    }

    if (seenLicensePlates.has(normalizedLicensePlate)) {
      ctx.addIssue({
        code: 'custom',
        message: t('parking-management:parkingModals.validation.licensePlateUnique'),
        path: [...pathPrefix, index, 'licensePlate'],
      })
      return
    }

    seenLicensePlates.add(normalizedLicensePlate)
  })
}

export const makeVehiclesArraySchema = (t: TFunction<['parking-management']>) =>
  makeBaseVehiclesArraySchema(t).superRefine((vehicles, ctx) => validateVehicles(vehicles, ctx, t))

export type FormMode = 'read' | 'edit'

// Note: Parking spot form
// Note: 'vehicles' is untyped at the object level — when the spot is effectively
// unassigned, its contents (e.g. stale useFieldArray entries) are never used, so
// they must not be structurally validated. Real shape/rule checks run in superRefine.
export const makeParkingSpotFormSchema = (t: TFunction<['parking-management']>) =>
  z
    .object({
      assignmentType: ParkingAssignmentTypeEnum,
      residentId: z.number().nullable(),
      unit: z.string().nullable(),
      vehicles: z.custom<VehicleFormData[]>(),
    })
    .superRefine((data, ctx) => {
      // Note: When resident/unit isn't selected, the spot is saved as Unassigned — skip all vehicle validation
      const isEffectivelyUnassigned =
        data.assignmentType === ParkingAssignmentTypeEnum.enum.Unassigned ||
        (data.assignmentType === ParkingAssignmentTypeEnum.enum.Unit && !data.unit) ||
        (data.assignmentType === ParkingAssignmentTypeEnum.enum.Resident && !data.residentId)

      if (isEffectivelyUnassigned) return

      const vehiclesResult = makeBaseVehiclesArraySchema(t).safeParse(data.vehicles)
      if (!vehiclesResult.success) {
        vehiclesResult.error.issues.forEach((issue) =>
          ctx.addIssue({ ...issue, path: ['vehicles', ...issue.path] }),
        )
        return
      }
      const vehicles = vehiclesResult.data

      validateVehicles(vehicles, ctx, t, ['vehicles'])

      if (data.assignmentType === ParkingAssignmentTypeEnum.enum.Unit) {
        vehicles.forEach((vehicle, index) => {
          if (!isEmptyVehicle(vehicle) && !vehicle.residentId) {
            ctx.addIssue({
              code: 'custom',
              message: t('parking-management:parkingModals.validation.residentRequired'),
              path: ['vehicles', index, 'residentId'],
            })
          }
        })
      }
    })
export type ParkingSpotFormData = z.infer<ReturnType<typeof makeParkingSpotFormSchema>>

// Note: Resident vehicles form
export const makeResidentVehiclesFormSchema = (t: TFunction<['parking-management']>) =>
  z.object({
    vehicles: makeVehiclesArraySchema(t),
  })
export type ResidentVehiclesFormData = z.infer<ReturnType<typeof makeResidentVehiclesFormSchema>>
